/**
 * BHERMES Metaverse — Digital Twin Generator (Phase 2)
 * AI-powered room analysis: upload/select hotel photos → GPT-5.2 Vision → 3D room config
 * Route: /metaverse/:hotelId/generate
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Sparkles, Loader2, Eye, Trash2,
  Camera, CheckCircle2, RefreshCw, Building2, Image as ImageIcon,
  SplitSquareHorizontal, Maximize2,
} from 'lucide-react';


import { buildRoomFromAIConfig } from '@/lib/buildRoomFromAI';
import { BACKEND_URL, API } from '@/lib/backendUrl';

/* ════════════════════════════════════════════════
   3D PREVIEW HOOK
   ════════════════════════════════════════════════ */

function usePreviewScene(containerRef, config, ready, layoutKey) {
  useEffect(() => {
    if (!ready || !containerRef.current || !config) return;
    const container = containerRef.current;

    // Small delay to ensure container has rendered with correct dimensions
    const timerId = setTimeout(() => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === 0 || h === 0) return;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0a0e1a);

      const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 100);
      camera.position.set(4, 3, 6);

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.2;
      renderer.shadowMap.enabled = true;
      container.appendChild(renderer.domElement);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.target.set(0, 1.2, 0);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.update();

      buildRoomFromAIConfig(scene, config);

      let animId;
      const animate = () => {
        animId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      container._cleanup = () => {
        cancelAnimationFrame(animId);
        controls.dispose();
        renderer.dispose();
        if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      };
    }, 50);

    return () => {
      clearTimeout(timerId);
      if (containerRef.current?._cleanup) {
        containerRef.current._cleanup();
        containerRef.current._cleanup = null;
      }
    };
  }, [containerRef, config, ready, layoutKey]);
}

/* ════════════════════════════════════════════════
   ROOM TYPE CARD
   ════════════════════════════════════════════════ */

function RoomTypeCard({ room, aiConfig, onAnalyze, onUpload, onDelete, onPreview, analyzing }) {
  const hasConfig = !!aiConfig;
  const confidence = aiConfig?.config?.confidence_score;

  return (
    <div
      className={`p-4 rounded-xl border transition-all cursor-pointer ${hasConfig ? 'border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
      data-testid={`room-card-${room.room_type_id}`}
      onClick={() => hasConfig && onPreview(room)}
    >      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm font-medium text-white">{room.room_type_name}</p>
          <p className="text-xs text-gray-500">${room.price_per_night}/night | {room.max_guests} guests</p>
        </div>
        {hasConfig ? (
          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-[10px] gap-1">
            <CheckCircle2 className="h-3 w-3" /> Generated
          </Badge>
        ) : (
          <Badge variant="outline" className="text-gray-500 border-white/10 text-[10px]">
            Template
          </Badge>
        )}
      </div>

      {hasConfig && confidence != null && (
        <div className="mb-3">
          <div className="flex justify-between text-[10px] text-gray-500 mb-1">
            <span>AI Confidence</span>
            <span>{Math.round(confidence * 100)}%</span>
          </div>
          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${confidence * 100}%` }} />
          </div>
        </div>
      )}

      {hasConfig && aiConfig.source_image_url && !aiConfig.source_image_url.startsWith('uploaded:') && (
        <div className="mb-3 text-[10px] text-gray-500 truncate">
          Source: {aiConfig.source_image_url.substring(0, 50)}...
        </div>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          className={`flex-1 text-xs gap-1 ${hasConfig ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-[#F7931A] hover:bg-[#F7931A]/90 text-black'}`}
          onClick={() => onAnalyze(room)}
          disabled={analyzing}
          data-testid={`analyze-btn-${room.room_type_id}`}
        >
          {analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {hasConfig ? 'Re-analyze' : 'Generate Twin'}
        </Button>

        <label className="cursor-pointer" data-testid={`upload-btn-${room.room_type_id}`}>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(room, e.target.files[0])} disabled={analyzing} />
          <div className={`h-8 px-2.5 rounded-md border flex items-center justify-center text-xs gap-1 transition-colors ${analyzing ? 'opacity-50 cursor-not-allowed' : 'border-white/10 text-gray-400 hover:text-white hover:border-white/20'}`}>
            <Upload className="h-3.5 w-3.5" />
          </div>
        </label>

        {hasConfig && (
          <Button size="sm" variant="ghost" className="text-red-400/60 hover:text-red-400 px-2" onClick={() => onDelete(room)} data-testid={`delete-btn-${room.room_type_id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════ */

export default function DigitalTwinGeneratorPage() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [aiConfigs, setAiConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null); // room_type_id being analyzed
  const [previewConfig, setPreviewConfig] = useState(null);
  const [previewSourceImage, setPreviewSourceImage] = useState(null);
  const [previewRoomName, setPreviewRoomName] = useState('');
  const [compareMode, setCompareMode] = useState(true);
  const previewRef = useRef(null);
  const [previewReady, setPreviewReady] = useState(false);

  usePreviewScene(previewRef, previewConfig, previewReady && !!previewConfig, compareMode);

  // Fetch hotel data & existing AI configs
  useEffect(() => {
    const load = async () => {
      try {
        const [hotelRes, roomsRes, configsRes] = await Promise.all([
          fetch(`${API}/api/hotels/${hotelId}/public`).then(r => r.json()),
          fetch(`${API}/api/hotels/${hotelId}/rooms`).then(r => r.json()),
          fetch(`${API}/api/metaverse/room-configs/${hotelId}`).then(r => r.json()),
        ]);
        setHotel(hotelRes.hotel || hotelRes);

        // Dedupe room types
        const allRooms = Array.isArray(roomsRes) ? roomsRes : (roomsRes.rooms || []);
        const seen = new Set();
        const unique = allRooms.filter(r => {
          const key = r.room_type_name || r.room_type_id;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setRooms(unique);
        setAiConfigs(configsRes.configs || []);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load hotel data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hotelId]);

  useEffect(() => { setPreviewReady(true); }, []);

  const getConfigForRoom = (room) => {
    return aiConfigs.find(c => c.room_type_name === room.room_type_name);
  };

  const handleAnalyze = async (room) => {
    setAnalyzing(room.room_type_id);
    try {
      const res = await fetch(`${API}/api/metaverse/analyze-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId,
          room_type_name: room.room_type_name,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Digital twin generated for ${room.room_type_name}`);
        setPreviewConfig(data.config);
        setPreviewSourceImage(data.source_image || null);
        setPreviewRoomName(room.room_type_name);
        setCompareMode(!!data.source_image && !data.source_image.startsWith('uploaded:'));
        // Refresh configs
        const cfgRes = await fetch(`${API}/api/metaverse/room-configs/${hotelId}`).then(r => r.json());
        setAiConfigs(cfgRes.configs || []);
      } else {
        toast.error(data.error || 'Analysis failed');
      }
    } catch {
      toast.error('Failed to analyze room');
    } finally {
      setAnalyzing(null);
    }
  };

  const handleUpload = async (room, file) => {
    setAnalyzing(room.room_type_id);
    const localPreviewUrl = URL.createObjectURL(file);
    try {
      const formData = new FormData();
      formData.append('hotel_id', hotelId);
      formData.append('room_type_name', room.room_type_name);
      formData.append('file', file);

      const res = await fetch(`${API}/api/metaverse/analyze-room/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Digital twin generated from uploaded photo for ${room.room_type_name}`);
        setPreviewConfig(data.config);
        setPreviewSourceImage(localPreviewUrl);
        setPreviewRoomName(room.room_type_name);
        setCompareMode(true);
        const cfgRes = await fetch(`${API}/api/metaverse/room-configs/${hotelId}`).then(r => r.json());
        setAiConfigs(cfgRes.configs || []);
      } else {
        toast.error(data.error || 'Upload analysis failed');
      }
    } catch {
      toast.error('Failed to analyze uploaded photo');
    } finally {
      setAnalyzing(null);
    }
  };

  const handleDelete = async (room) => {
    try {
      const res = await fetch(`${API}/api/metaverse/room-configs/${hotelId}/${encodeURIComponent(room.room_type_name)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success(`Reverted ${room.room_type_name} to template`);
        setAiConfigs(prev => prev.filter(c => c.room_type_name !== room.room_type_name));
        if (previewRoomName === room.room_type_name) {
          setPreviewConfig(null);
          setPreviewSourceImage(null);
          setPreviewRoomName('');
        }
      }
    } catch {
      toast.error('Failed to delete config');
    }
  };

  const handlePreview = (room) => {
    const cfg = getConfigForRoom(room);
    if (cfg) {
      setPreviewConfig(cfg.config);
      const src = cfg.source_image_url;
      setPreviewSourceImage(src && !src.startsWith('uploaded:') ? src : null);
      setPreviewRoomName(room.room_type_name);
      setCompareMode(!!src && !src.startsWith('uploaded:'));
    }
  };

  const generatedCount = aiConfigs.length;
  const totalRoomTypes = rooms.length;

  if (loading) {
    return (
      <div className="h-screen bg-[#0a0e1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F7931A]" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0a0e1a] flex flex-col overflow-hidden" data-testid="digital-twin-generator">
      {/* Top Bar */}
      <div className="h-14 border-b border-white/5 bg-[#0a0e1a]/90 backdrop-blur-xl flex items-center justify-between px-4 z-20 shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="h-7 w-7 rounded bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
            <span className="text-white font-bold text-[10px]">B</span>
          </div>
          <div>
            <p className="text-sm font-medium text-white">{hotel?.name || 'Hotel'}</p>
            <p className="text-[10px] text-gray-500">Digital Twin Generator — Phase 2</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-[#F7931A]/30 text-[#F7931A] bg-[#F7931A]/5 text-[10px] gap-1">
            <Sparkles className="h-3 w-3" /> AI-Powered
          </Badge>
          {generatedCount > 0 && (
            <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-[10px] gap-1">
              <CheckCircle2 className="h-3 w-3" /> {generatedCount}/{totalRoomTypes}
            </Badge>
          )}
          <Button
            size="sm"
            className="text-xs bg-[#F7931A]/10 text-[#F7931A] hover:bg-[#F7931A]/20 border border-[#F7931A]/20 gap-1"
            onClick={() => navigate(`/metaverse/${hotelId}`)}
            data-testid="view-metaverse-btn"
          >
            <Eye className="h-3.5 w-3.5" /> View Metaverse
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Room Types */}
        <div className="w-96 border-r border-white/5 bg-[#0c1020] overflow-y-auto p-4 space-y-4 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500">Room Types</p>
              <p className="text-[10px] text-gray-600 mt-0.5">{totalRoomTypes} types | {generatedCount} twins generated</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-[10px] text-gray-500 hover:text-white gap-1"
              onClick={() => {
                const cfgRes = fetch(`${API}/api/metaverse/room-configs/${hotelId}`).then(r => r.json()).then(d => setAiConfigs(d.configs || []));
              }}
              data-testid="refresh-configs-btn"
            >
              <RefreshCw className="h-3 w-3" /> Refresh
            </Button>
          </div>

          <div className="space-y-3" data-testid="room-types-list">
            {rooms.map(room => (
              <RoomTypeCard
                key={room.room_type_id}
                room={room}
                aiConfig={getConfigForRoom(room)}
                onAnalyze={handleAnalyze}
                onUpload={handleUpload}
                onDelete={handleDelete}
                onPreview={handlePreview}
                analyzing={analyzing === room.room_type_id}
              />
            ))}
          </div>

          {rooms.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No room types found</p>
            </div>
          )}
        </div>

        {/* Right: Before/After Preview */}
        <div className="flex-1 relative">
          {previewConfig ? (
            <>
              {/* Mode toggle */}
              <div className="absolute top-4 right-4 z-20 flex gap-1.5" data-testid="preview-mode-toggle">
                {previewSourceImage && (
                  <button
                    onClick={() => setCompareMode(true)}
                    className={`h-7 px-2.5 rounded-md text-[10px] font-medium flex items-center gap-1 transition-all ${compareMode ? 'bg-[#F7931A] text-black' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'}`}
                    data-testid="compare-mode-btn"
                  >
                    <SplitSquareHorizontal className="h-3 w-3" /> Before / After
                  </button>
                )}
                <button
                  onClick={() => setCompareMode(false)}
                  className={`h-7 px-2.5 rounded-md text-[10px] font-medium flex items-center gap-1 transition-all ${!compareMode ? 'bg-[#F7931A] text-black' : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'}`}
                  data-testid="fullscreen-mode-btn"
                >
                  <Maximize2 className="h-3 w-3" /> Full 3D
                </button>
              </div>

              {/* 3D Canvas — always full width behind everything */}
              <div
                ref={previewRef}
                className="absolute inset-0"
                style={compareMode && previewSourceImage ? { left: '50%', width: '50%' } : {}}
                data-testid="preview-canvas"
              />

              {compareMode && previewSourceImage ? (
                /* ─── BEFORE / AFTER SPLIT VIEW ─── */
                <div className="absolute inset-0 pointer-events-none" data-testid="compare-view">
                  {/* LEFT: Original Photo */}
                  <div className="absolute inset-y-0 left-0 w-1/2 border-r border-white/10 bg-[#080c16] overflow-hidden pointer-events-auto">
                    <div className="absolute top-4 left-4 z-10">
                      <Badge className="bg-black/60 backdrop-blur-sm text-gray-300 border-white/10 text-[10px] gap-1">
                        <ImageIcon className="h-3 w-3" /> Original Photo
                      </Badge>
                    </div>
                    <img
                      src={previewSourceImage}
                      alt={`${previewRoomName} original`}
                      className="w-full h-full object-cover"
                      data-testid="source-image"
                    />
                    <div className="absolute bottom-4 left-4 right-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-gray-400" data-testid="source-info">
                      <p className="text-white font-medium mb-0.5">{previewRoomName}</p>
                      <p>Source photo used for AI analysis</p>
                    </div>
                  </div>

                  {/* CENTER: Divider */}
                  <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-white/20 z-10">
                    <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full border border-white/20 bg-[#0a0e1a] flex items-center justify-center">
                      <SplitSquareHorizontal className="h-4 w-4 text-[#F7931A]" />
                    </div>
                  </div>

                  {/* RIGHT: Labels for 3D side */}
                  <div className="absolute top-4 right-auto z-10" style={{ left: 'calc(50% + 16px)' }}>
                    <Badge className="bg-[#F7931A]/20 backdrop-blur-sm text-[#F7931A] border-[#F7931A]/30 text-[10px] gap-1">
                      <Sparkles className="h-3 w-3" /> AI Digital Twin
                    </Badge>
                  </div>
                  <div className="absolute bottom-4 z-10 pointer-events-none" style={{ left: 'calc(50% + 16px)', right: '16px' }}>
                    <div className="bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-gray-400" data-testid="preview-info">
                      <p className="text-white font-medium mb-0.5">AI-Generated 3D Model</p>
                      <p>
                        {previewConfig.estimated_dimensions?.width_meters}m x {previewConfig.estimated_dimensions?.depth_meters}m |{' '}
                        {previewConfig.furniture?.length || 0} furniture |{' '}
                        {Math.round((previewConfig.confidence_score || 0) * 100)}% confidence
                      </p>
                      {previewConfig.style_notes && <p className="text-gray-500 italic mt-1 line-clamp-2">{previewConfig.style_notes}</p>}
                    </div>
                  </div>
                </div>
              ) : (
                /* ─── FULL 3D: Info overlay ─── */
                <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-gray-400 space-y-1 z-10" data-testid="preview-info">
                  <p className="text-white font-medium">{previewRoomName || 'AI-Generated Preview'}</p>
                  <p>Dimensions: {previewConfig.estimated_dimensions?.width_meters}m x {previewConfig.estimated_dimensions?.depth_meters}m x {previewConfig.estimated_dimensions?.height_meters}m</p>
                  <p>Furniture: {previewConfig.furniture?.length || 0} items</p>
                  <p>Confidence: {Math.round((previewConfig.confidence_score || 0) * 100)}%</p>
                  {previewConfig.style_notes && <p className="text-gray-500 italic">{previewConfig.style_notes}</p>}
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center" data-testid="preview-placeholder">
              <div className="text-center">
                <div className="h-16 w-16 rounded-2xl bg-[#F7931A]/10 flex items-center justify-center mx-auto mb-4">
                  <Camera className="h-8 w-8 text-[#F7931A]/60" />
                </div>
                <p className="text-sm text-gray-400 mb-1">Select a room type and generate its digital twin</p>
                <p className="text-xs text-gray-600">AI will analyze the room photo and build a 3D model</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
